import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Share2, Upload } from "lucide-react";
import {
  Button,
  cn,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@nexus/ui";
import { toast } from "sonner";
import { useAuth } from "@/features/auth/hooks/useAuth";
import { useIsSuperTenant } from "@/features/auth/hooks/usePermissions";
import {
  trackScriptReviewReportViewed,
  trackScriptReviewShareLinkCreated,
  trackScriptReviewShareLinkRevoked,
  trackScriptReviewShareLinkCopied,
  trackScriptReviewNotePosted,
  trackScriptReviewCommentDeleted,
  trackScriptReviewCreated,
  trackScriptReviewCommentResolved,
  trackScriptReviewReadyChanged,
} from "@/lib/analytics-events";
import {
  scriptReviewDetailQueryOptions,
  scriptReviewStatusQueryOptions,
  scriptReviewKeys,
} from "@/lib/queries/script-review.queries";
import { CollapsibleSection } from "@/features/script-review/components/CollapsibleSection";
import { ScriptReviewPlayer } from "@/features/script-review/components/ScriptReviewPlayer";
import { useStableVideoUrl } from "@/features/script-review/lib/use-stable-video-url";
import { translateScriptReviewStatusLabel } from "@/features/script-review/lib/script-review-status";
import { toastScriptReviewError } from "@/features/script-review/lib/toast-script-review-error";
import { GENERIC_API_ERROR_I18N_KEY } from "@/lib/api-client";
import {
  isTerminalScriptReviewStatus,
  postScriptReviewNote,
  createScriptReviewShareLink,
  revokeScriptReviewShareLink,
  deleteScriptReviewComment,
  deleteScriptReviewNote,
  runScriptReviewUploadFlow,
  createScriptReview,
  setScriptReviewCommentResolution,
  setScriptReviewNoteResolution,
  setScriptReviewReady,
  type PostNoteTarget,
  type ScriptReviewReadiness,
} from "@/features/script-review/api/script-review.api";

export interface ScriptReviewDetailPageProps {
  reviewId: string;
  onBack: () => void;
  /** Navigate to another review id (version switch or a just-created version). */
  onOpenVersion?: (reviewId: string) => void;
}

function formatSecond(s: number): string {
  const total = Math.max(0, Math.round(s));
  const mm = Math.floor(total / 60);
  const ss = total % 60;
  return `${mm}:${ss.toString().padStart(2, "0")}`;
}

/**
 * Share link, in a popover: getting a link is a light, read-mostly action, and a
 * popover keeps the video visible behind it instead of blocking the page.
 */
function SharePopover({
  shareToken,
  shareUrl,
  creating,
  revoking,
  onCreate,
  onRevoke,
  onCopy,
}: {
  shareToken: string | null;
  shareUrl: string;
  creating: boolean;
  revoking: boolean;
  onCreate: () => void;
  onRevoke: () => void;
  onCopy: () => void;
}) {
  const { t } = useTranslation();
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button size="sm" variant="outline" className="h-8">
          <Share2 className="mr-1.5 h-3.5 w-3.5" aria-hidden />
          {t("scriptReview.share.button", "Share")}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="flex w-80 flex-col gap-3">
        <div className="flex flex-col gap-0.5">
          <h3 className="text-sm font-semibold">
            {t("scriptReview.share.heading", "Share with vendor")}
          </h3>
          <p className="text-xs text-muted-foreground">
            {t(
              "scriptReview.share.hint",
              "Anyone with the link can watch the video, see comments, and reply — no Adge account needed.",
            )}
          </p>
        </div>

        {!shareToken ? (
          <Button size="sm" onClick={onCreate} disabled={creating}>
            {creating
              ? t("scriptReview.share.creating", "Creating…")
              : t("scriptReview.share.create", "Create share link")}
          </Button>
        ) : (
          <>
            <div className="flex items-center gap-2">
              <input
                readOnly
                value={shareUrl}
                onFocus={(e) => e.currentTarget.select()}
                className="flex-1 rounded-md border border-border bg-background px-2 py-1 text-xs text-muted-foreground"
              />
              <Button size="sm" variant="outline" onClick={onCopy}>
                {t("scriptReview.share.copy", "Copy")}
              </Button>
            </div>
            <Button
              size="sm"
              variant="ghost"
              className="self-start text-destructive hover:text-destructive"
              onClick={onRevoke}
              disabled={revoking}
            >
              {t("scriptReview.share.revoke", "Revoke")}
            </Button>
          </>
        )}
      </PopoverContent>
    </Popover>
  );
}

/**
 * Upload a new version, in a dialog: unlike sharing this commits real work (it
 * kicks off a fresh AI analysis and moves you to a new version), so it gets a
 * focused modal with an explicit confirm rather than an always-open form.
 */
function UploadVersionDialog({
  file,
  uploading,
  onPick,
  onUpload,
}: {
  file: File | null;
  uploading: boolean;
  onPick: (file: File | null) => void;
  onUpload: () => Promise<void>;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (uploading) return; // don't let a click-away abandon an in-flight upload
        setOpen(next);
        if (!next) onPick(null);
      }}
    >
      <DialogTrigger asChild>
        <Button size="sm" variant="outline" className="h-8">
          <Upload className="mr-1.5 h-3.5 w-3.5" aria-hidden />
          {t("scriptReview.version.uploadCta", "Analyze new version")}
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("scriptReview.version.uploadHeading", "Upload a new version")}</DialogTitle>
          <DialogDescription>
            {t(
              "scriptReview.version.uploadHint",
              "Got a revised cut back? Upload it and the AI re-checks it against this version's comments.",
            )}
          </DialogDescription>
        </DialogHeader>
        <input
          type="file"
          accept="video/mp4,video/quicktime"
          disabled={uploading}
          onChange={(e) => onPick(e.target.files?.[0] ?? null)}
          className="text-sm file:mr-3 file:rounded-md file:border file:border-border file:bg-muted file:px-3 file:py-1.5 file:text-sm"
        />
        <DialogFooter>
          <Button variant="ghost" onClick={() => setOpen(false)} disabled={uploading}>
            {t("common.cancel", "Cancel")}
          </Button>
          <Button
            disabled={!file || uploading}
            onClick={async () => {
              await onUpload();
              setOpen(false);
            }}
          >
            {uploading
              ? t("scriptReview.version.uploading", "Uploading…")
              : t("scriptReview.version.uploadCta", "Analyze new version")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** One "x / y resolved" row with a progress bar. */
function ReadinessRow({
  label,
  resolved,
  total,
  met,
  hint,
}: {
  label: string;
  resolved: number;
  total: number;
  met: boolean;
  hint?: string;
}) {
  const pct = total === 0 ? 100 : Math.round((resolved / total) * 100);
  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-baseline justify-between gap-3 text-xs">
        <span className="font-medium">{label}</span>
        <span className={cn("tabular-nums", met ? "text-emerald-600 dark:text-emerald-400" : "text-muted-foreground")}>
          {resolved}/{total}
          {hint ? ` · ${hint}` : ""}
        </span>
      </div>
      <div className="h-1.5 w-full overflow-hidden rounded-full bg-muted">
        <div
          className={cn("h-full rounded-full transition-all", met ? "bg-emerald-500" : "bg-primary")}
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}

/**
 * Readiness panel — shows the two criteria and the client-review sign-off.
 * The server computes `readiness` and re-validates on write, so this is a
 * presentation of the server's verdict, never the decision itself.
 */
function ReadinessCard({
  readiness,
  readyForClientAt,
  canMark,
  pending,
  onSetReady,
}: {
  readiness: ScriptReviewReadiness;
  readyForClientAt: string | null;
  canMark: boolean;
  pending: boolean;
  onSetReady: (ready: boolean) => void;
}) {
  const { t } = useTranslation();
  const isReady = readyForClientAt != null;
  // Marked ready, but comments have since arrived / been unresolved. The sign-off
  // stays (it's a record of a human decision) — we surface the drift instead.
  const stale = isReady && !readiness.isEligible;

  return (
    <section className="flex flex-col gap-3 rounded-lg border border-border bg-card p-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-col">
          <h2 className="text-sm font-semibold">
            {t("scriptReview.readiness.heading", "Client readiness")}
          </h2>
          <p className="text-xs text-muted-foreground">
            {t(
              "scriptReview.readiness.hint",
              "Resolve every team comment, and AI comments up to the threshold, before showing this to the client.",
            )}
          </p>
        </div>
        {isReady ? (
          <div className="flex items-center gap-2">
            <span className="rounded-full border border-emerald-500/30 bg-emerald-500/10 px-2 py-0.5 text-xs font-medium text-emerald-700 dark:text-emerald-300">
              {t("scriptReview.readiness.readyBadge", "Ready for client review")}
            </span>
            {canMark && (
              <Button size="sm" variant="ghost" disabled={pending} onClick={() => onSetReady(false)}>
                {t("scriptReview.readiness.unmark", "Unmark")}
              </Button>
            )}
          </div>
        ) : (
          canMark && (
            <Button
              size="sm"
              disabled={!readiness.isEligible || pending}
              onClick={() => onSetReady(true)}
            >
              {pending
                ? t("scriptReview.readiness.marking", "Marking…")
                : t("scriptReview.readiness.markReady", "Mark ready for client review")}
            </Button>
          )
        )}
      </div>

      <div className="flex flex-col gap-2">
        <ReadinessRow
          label={t("scriptReview.readiness.humanRow", "Team comments")}
          resolved={readiness.humanResolved}
          total={readiness.humanTotal}
          met={readiness.humanResolved === readiness.humanTotal}
          hint={t("scriptReview.readiness.allRequired", "all required")}
        />
        <ReadinessRow
          label={t("scriptReview.readiness.aiRow", "AI comments")}
          resolved={readiness.aiResolved}
          total={readiness.aiTotal}
          met={readiness.aiTotal === 0 || readiness.aiResolvedPct >= readiness.thresholdPct}
          hint={t("scriptReview.readiness.thresholdHint", "needs {{pct}}%", {
            pct: readiness.thresholdPct,
          })}
        />
      </div>

      {stale && (
        <p className="rounded-md border border-amber-500/40 bg-amber-500/10 p-2 text-xs text-amber-700 dark:text-amber-300">
          {t(
            "scriptReview.readiness.staleWarning",
            "Unresolved comments have appeared since this was marked ready — re-check before sharing.",
          )}
        </p>
      )}
      {!isReady && !readiness.isEligible && (
        <p className="text-xs text-muted-foreground">
          {t("scriptReview.readiness.notEligibleHint", "Resolve the remaining comments to enable sign-off.")}
        </p>
      )}
    </section>
  );
}

export function ScriptReviewDetailPage({
  reviewId,
  onBack,
  onOpenVersion,
}: ScriptReviewDetailPageProps) {
  const { t } = useTranslation();
  const activeTenantId = useAuth((s) => s.activeTenantId);
  // Adge-admin-only moderation. The player renders delete affordances only when
  // these callbacks are passed, so org users (and the guest page) never see them.
  const isSuperTenant = useIsSuperTenant();
  const qc = useQueryClient();
  const detailQuery = scriptReviewDetailQueryOptions(activeTenantId, reviewId);
  const { data, isLoading, error } = useQuery(detailQuery);
  // Every refetch re-signs the video URL; pin it so mutations never reload the
  // player (which would reset playback and scroll the comment panel away).
  const videoUrl = useStableVideoUrl(reviewId, data?.videoUrl);
  // Same treatment for the iteration reference clip (null in script mode).
  const referenceVideoUrl = useStableVideoUrl(reviewId, data?.referenceVideoUrl);
  /** Which context this review was judged against — an analytics dimension. */
  const scriptSource = data
    ? data.mode === "ITERATION"
      ? "iteration"
      : data.scriptUploadId
        ? "upload"
        : "set"
    : "set";

  // Live-poll while the loaded review is non-terminal (e.g. a just-uploaded
  // version going QUEUED → ANALYZING → COMPLETED). Seeded once the review is
  // known to be in progress; the ramp stops itself at a terminal status.
  const [pollStartedAt, setPollStartedAt] = useState(0);
  const statusQuery = useQuery(
    scriptReviewStatusQueryOptions(activeTenantId, reviewId, pollStartedAt),
  );
  useEffect(() => {
    if (data && !isTerminalScriptReviewStatus(data.status)) {
      setPollStartedAt((prev) => (prev === 0 ? Date.now() : prev));
    } else if (data) {
      setPollStartedAt(0);
    }
  }, [data]);
  // When the poll observes a status the detail query hasn't caught up to, refetch
  // the full detail so comments/versions/videoUrl appear.
  const polledStatus = statusQuery.data?.status;
  const prevPolledStatusRef = useRef<string | undefined>(undefined);
  const failedToastShownRef = useRef(false);

  useEffect(() => {
    failedToastShownRef.current = false;
    prevPolledStatusRef.current = undefined;
  }, [reviewId]);

  useEffect(() => {
    const status = polledStatus ?? data?.status;
    if (!status) return;
    const prev = prevPolledStatusRef.current;
    prevPolledStatusRef.current = status;
    if (status === "FAILED" && prev && prev !== "FAILED" && !failedToastShownRef.current) {
      failedToastShownRef.current = true;
      toast.error(t(GENERIC_API_ERROR_I18N_KEY));
    }
  }, [polledStatus, data?.status, t]);

  useEffect(() => {
    if (polledStatus && data && polledStatus !== data.status) {
      void qc.invalidateQueries({ queryKey: detailQuery.queryKey });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [polledStatus]);

  // "Upload new version" state (only the latest, terminal version can spawn one).
  const [versionFile, setVersionFile] = useState<File | null>(null);
  const [uploadingVersion, setUploadingVersion] = useState(false);

  // Fire "report viewed" once per mounted review. The detail query is
  // invalidated after every note post / share change / moderation delete, so
  // `data` identity changes on refetch — the ref (keyed by reviewId) is what
  // prevents re-fires. A user landing while ANALYZING gets the event the moment
  // the refetch flips to COMPLETED; a back-nav remount counts as a new view.
  const viewTrackedRef = useRef<string | null>(null);
  useEffect(() => {
    if (!data || data.status !== "COMPLETED" || viewTrackedRef.current === reviewId) return;
    viewTrackedRef.current = reviewId;
    trackScriptReviewReportViewed({
      review_id: reviewId,
      comment_count: data.comments.length,
      note_count: data.notes.length,
      script_source: scriptSource,
      has_share_link: !!data.shareToken,
    });
  }, [data, reviewId]);

  const invalidate = () => qc.invalidateQueries({ queryKey: detailQuery.queryKey });

  const postNote = async (name: string, body: string, target: PostNoteTarget) => {
    try {
      await postScriptReviewNote(reviewId, { name, body, ...target });
      await invalidate();
      trackScriptReviewNotePosted({
        review_id: reviewId,
        note_kind: "aiCommentId" in target ? "reply" : "standalone",
        body_length: body.length,
      });
    } catch (e) {
      toastScriptReviewError(e);
      throw e;
    }
  };

  // Delete refreshes the detail view and the hub list (its `commentCount`
  // column). Invalidating the shared prefix covers both regardless of how the
  // list key normalizes the tenant id. Re-throws so the player's confirm dialog
  // stays open on failure.
  const afterModeration = () => qc.invalidateQueries({ queryKey: scriptReviewKeys.all });
  const handleDeleteComment = async (commentId: string, reason?: string) => {
    try {
      await deleteScriptReviewComment(reviewId, commentId, reason);
      await afterModeration();
      trackScriptReviewCommentDeleted({
        review_id: reviewId,
        comment_kind: "ai_comment",
        comment_id: commentId,
        has_reason: !!reason,
      });
      toast.success(
        reason
          ? t("scriptReview.toast.deletedWithReason", "Comment removed — improvement ticket created")
          : t("scriptReview.toast.deleted", "Comment removed"),
      );
    } catch (e) {
      toastScriptReviewError(e);
      throw e;
    }
  };
  const handleDeleteNote = async (noteId: string) => {
    try {
      await deleteScriptReviewNote(reviewId, noteId);
      await afterModeration();
      trackScriptReviewCommentDeleted({
        review_id: reviewId,
        comment_kind: "note",
        comment_id: noteId,
      });
      toast.success(t("scriptReview.toast.deleted", "Comment removed"));
    } catch (e) {
      toastScriptReviewError(e);
      throw e;
    }
  };

  // ── Readiness: resolution toggles + the client-review sign-off ─────
  // Silent on success (the card + chip are the feedback); toasts only on failure.
  // Re-throws so the player's toggle can restore its previous state.
  const handleResolveComment = async (commentId: string, resolved: boolean) => {
    try {
      await setScriptReviewCommentResolution(reviewId, commentId, resolved);
      await invalidate();
      trackScriptReviewCommentResolved({
        review_id: reviewId,
        comment_kind: "ai_comment",
        comment_id: commentId,
        resolved,
      });
    } catch (e) {
      toastScriptReviewError(e);
      throw e;
    }
  };
  const handleResolveNote = async (noteId: string, resolved: boolean) => {
    try {
      await setScriptReviewNoteResolution(reviewId, noteId, resolved);
      await invalidate();
      trackScriptReviewCommentResolved({
        review_id: reviewId,
        comment_kind: "note",
        comment_id: noteId,
        resolved,
      });
    } catch (e) {
      toastScriptReviewError(e);
      throw e;
    }
  };

  const readyMut = useMutation({
    mutationFn: (ready: boolean) => setScriptReviewReady(reviewId, ready),
    onSuccess: (_res, ready) => {
      if (data) {
        trackScriptReviewReadyChanged({
          review_id: reviewId,
          ready,
          version_number: data.version,
          ai_resolved_pct: data.readiness.aiResolvedPct,
          human_total: data.readiness.humanTotal,
          ai_total: data.readiness.aiTotal,
        });
      }
      // Refresh the detail + the hub list (its Ready badge).
      void qc.invalidateQueries({ queryKey: scriptReviewKeys.all });
      toast.success(
        ready
          ? t("scriptReview.readiness.markedReady", "Marked ready for client review")
          : t("scriptReview.readiness.unmarked", "Readiness removed"),
      );
    },
    // The server re-validates the criteria — surface its precise 409 message.
    onError: (e) => toastScriptReviewError(e),
  });

  const shareMut = useMutation({
    mutationFn: () => createScriptReviewShareLink(reviewId),
    onSuccess: () => {
      trackScriptReviewShareLinkCreated({ review_id: reviewId });
      invalidate();
    },
    onError: (e) => toastScriptReviewError(e),
  });
  const revokeMut = useMutation({
    mutationFn: () => revokeScriptReviewShareLink(reviewId),
    onSuccess: () => {
      trackScriptReviewShareLinkRevoked({ review_id: reviewId });
      invalidate();
    },
    onError: (e) => toastScriptReviewError(e),
  });

  const shareUrl =
    data?.shareToken && typeof window !== "undefined"
      ? `${window.location.origin}/sr/${data.shareToken}`
      : "";
  const copyShareUrl = async () => {
    if (!shareUrl) return;
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast.success(t("scriptReview.share.copied", "Share link copied"));
      trackScriptReviewShareLinkCopied({ review_id: reviewId });
    } catch {
      toast.error(
        t(
          "scriptReview.errors.copyShareLink",
          "Could not copy — select the link and copy manually",
        ),
      );
    }
  };

  // Upload a new video version: upload → create a linked follow-up review → jump
  // to it (which then live-polls to COMPLETED). Script source is inherited server-side.
  const uploadNewVersion = async () => {
    if (!activeTenantId || !versionFile) return;
    setUploadingVersion(true);
    try {
      const uploadId = await runScriptReviewUploadFlow({ tenantId: activeTenantId, file: versionFile });
      const { reviewId: newId } = await createScriptReview({ uploadId, previousReviewId: reviewId });
      trackScriptReviewCreated({
        review_id: newId,
        script_source: scriptSource,
        video_size_bytes: versionFile.size,
        previous_review_id: reviewId,
      });
      setVersionFile(null);
      await qc.invalidateQueries({ queryKey: scriptReviewKeys.all });
      toast.success(t("scriptReview.version.created", "New version uploaded — analyzing…"));
      onOpenVersion?.(newId);
    } catch (e) {
      toastScriptReviewError(e);
    } finally {
      setUploadingVersion(false);
    }
  };

  const segments = data?.transcript?.segments ?? [];
  const isTerminal = data ? isTerminalScriptReviewStatus(data.status) : false;

  return (
    <div className="container mx-auto flex max-w-5xl flex-col gap-6 p-6">
      <Button variant="ghost" onClick={onBack} className="-ml-3 self-start">
        ← {t("scriptReview.back", "Back")}
      </Button>

      {/* Title + the page's actions in one bar, so the player sits near the top
          instead of below two always-open forms. */}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <header className="flex flex-col gap-1">
          <h1 className="text-xl font-semibold tracking-tight">
            {t("scriptReview.reportTitle", "Pre-flight report")}
          </h1>
          {data && (
            <p className="text-sm text-muted-foreground">
              {data.version > 1 &&
                `${t("scriptReview.version.label", "Version {{n}}", { n: data.version })} · `}
              {translateScriptReviewStatusLabel(t, data.status)} ·{" "}
              {t("scriptReview.meta.commentCount", {
                count: data.comments.length,
                defaultValue: "{{count}} comments",
              })}
            </p>
          )}
        </header>

        <div className="flex flex-wrap items-center gap-2">
          {data && data.versions.length > 1 && (
            <Select
              value={reviewId}
              onValueChange={(v) => {
                if (v !== reviewId) onOpenVersion?.(v);
              }}
            >
              <SelectTrigger
                className="h-8 w-[170px] text-xs"
                aria-label={t("scriptReview.version.switch", "Switch version")}
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {data.versions.map((v, i) => (
                  <SelectItem key={v.id} value={v.id}>
                    {t("scriptReview.version.label", "Version {{n}}", { n: v.version })}
                    {i === 0 ? ` · ${t("scriptReview.version.latest", "Latest")}` : ""}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          {data?.status === "COMPLETED" && (
            <SharePopover
              shareToken={data.shareToken}
              shareUrl={shareUrl}
              creating={shareMut.isPending}
              revoking={revokeMut.isPending}
              onCreate={() => shareMut.mutate()}
              onRevoke={() => revokeMut.mutate()}
              onCopy={copyShareUrl}
            />
          )}
          {data && data.isLatest && isTerminal && (
            <UploadVersionDialog
              file={versionFile}
              uploading={uploadingVersion}
              onPick={setVersionFile}
              onUpload={uploadNewVersion}
            />
          )}
        </div>
      </div>

      {isLoading && (
        <p className="text-sm text-muted-foreground">{t("scriptReview.loading", "Loading…")}</p>
      )}
      {error && (
        <p className="text-sm text-destructive">
          {t("scriptReview.loadError", "Failed to load the report.")}
        </p>
      )}

      {data?.status === "FAILED" && (
        <p className="text-sm text-muted-foreground">
          {t("scriptReview.hub.reviewFailed", "The review failed. Please try again.")}
        </p>
      )}

      {data && !isTerminal && (
        <p className="text-sm text-muted-foreground">
          {t(
            "scriptReview.inProgress",
            "Analysis in progress — step outputs will appear here once it completes.",
          )}
        </p>
      )}

      {data && data.status === "COMPLETED" && (
        <>
          {/* Readiness: is this cut clean enough to show the client? All team
              comments must be resolved; AI comments must clear the org threshold.
              Latest version only — readiness asks "can we send THIS to the
              client", which a superseded cut can never be the answer to. */}
          {data.isLatest && (
            <ReadinessCard
              readiness={data.readiness}
              readyForClientAt={data.readyForClientAt}
              canMark={isSuperTenant}
              pending={readyMut.isPending}
              onSetReady={(ready) => readyMut.mutate(ready)}
            />
          )}

          {/* Frame.io-style review player: video + timeline comments + collaboration.
              The main work surface, so it sits directly under the readiness panel —
              share/upload live in the header bar rather than pushing it down. */}
          <ScriptReviewPlayer
            videoUrl={videoUrl}
            aiComments={data.comments}
            notes={data.notes}
            failedPasses={data.failedPasses}
            canComment
            allowStandaloneNotes
            onPostNote={postNote}
            onDeleteComment={isSuperTenant ? handleDeleteComment : undefined}
            onDeleteNote={isSuperTenant ? handleDeleteNote : undefined}
            onResolveComment={isSuperTenant ? handleResolveComment : undefined}
            onResolveNote={isSuperTenant ? handleResolveNote : undefined}
          />
        </>
      )}

      {data && isTerminal && (
        <>
          {/* Iteration mode: what the video was judged against — the brief the
              vendor was given + the reference video it had to live up to. */}
          {data.mode === "ITERATION" && (
            <section className="flex flex-col gap-2">
              <h2 className="text-sm font-semibold tracking-tight">
                {t("scriptReview.iteration.heading", "Briefed against")}
              </h2>
              <CollapsibleSection
                title={t("scriptReview.iteration.sectionLabel", "Brief & reference video")}
              >
                <div className="flex flex-col gap-3">
                  <p className="whitespace-pre-wrap rounded-md border border-border bg-card p-3 text-sm">
                    {data.brief}
                  </p>
                  {referenceVideoUrl ? (
                    <video
                      controls
                      preload="metadata"
                      src={referenceVideoUrl}
                      className="max-h-72 w-full rounded-md border border-border bg-black"
                    />
                  ) : (
                    <p className="text-xs text-muted-foreground">
                      {t(
                        "scriptReview.iteration.videoUnavailable",
                        "The reference video is no longer available.",
                      )}
                    </p>
                  )}
                </div>
              </CollapsibleSection>
            </section>
          )}

          {/* The script the video was compared against (set script or parsed
              upload). Absent in iteration mode — the section self-hides. */}
          {data.scriptScenes && data.scriptScenes.scenes.length > 0 && (
            <section className="flex flex-col gap-2">
              <h2 className="text-sm font-semibold tracking-tight">
                {t("scriptReview.scriptHeading", "Script compared against")}
              </h2>
              <CollapsibleSection
                title={t("scriptReview.scriptScenesLabel", "Scenes")}
                count={data.scriptScenes.scenes.length}
              >
                <ul className="flex flex-col gap-2">
                  {data.scriptScenes.scenes.map((sc, i) => (
                    <li key={i} className="rounded-md border border-border bg-card p-3 text-sm">
                      <div className="mb-1 font-medium">
                        {sc.sceneNumber != null ? sc.sceneNumber : i + 1}. {sc.sceneName}
                      </div>
                      <div className="flex flex-col gap-0.5 text-xs text-muted-foreground">
                        {sc.hearing && (
                          <span>
                            <span className="font-medium">
                              {t("scriptReview.scriptScenes.hearing", "Hearing:")}
                            </span>{" "}
                            {sc.hearing}
                          </span>
                        )}
                        {sc.seeing && (
                          <span>
                            <span className="font-medium">
                              {t("scriptReview.scriptScenes.seeing", "Seeing:")}
                            </span>{" "}
                            {sc.seeing}
                          </span>
                        )}
                        {sc.props && (
                          <span>
                            <span className="font-medium">
                              {t("scriptReview.scriptScenes.props", "Props:")}
                            </span>{" "}
                            {sc.props}
                          </span>
                        )}
                      </div>
                    </li>
                  ))}
                </ul>
              </CollapsibleSection>
            </section>
          )}

          {/* The transcript that anchors the timeline (the comments themselves
              live on the player above — no separate per-step breakdown). */}
          <section className="flex flex-col gap-2">
            <h2 className="text-sm font-semibold tracking-tight">
              {t("scriptReview.steps.transcript", "Transcript")}
            </h2>
            <CollapsibleSection
              title={t("scriptReview.transcriptLabel", "Segments")}
              count={segments.length}
            >
              {segments.length === 0 ? (
                <p className="text-sm text-muted-foreground">
                  {t("scriptReview.steps.noTranscript", "No transcript available for this review.")}
                </p>
              ) : (
                <ul className="flex max-h-80 flex-col gap-1 overflow-y-auto pr-1">
                  {segments.map((seg, i) => (
                    <li
                      key={i}
                      className="flex gap-3 rounded-md border border-border bg-card p-2 text-sm"
                    >
                      <span className="shrink-0 font-mono text-xs text-muted-foreground">
                        {formatSecond(seg.startSecond)}–{formatSecond(seg.endSecond)}
                      </span>
                      <span>{seg.text}</span>
                    </li>
                  ))}
                </ul>
              )}
            </CollapsibleSection>
          </section>
        </>
      )}
    </div>
  );
}
